import { Chip, Box, Tooltip, IconButton } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';
import moment from 'moment/moment';

import {
  IcAddNote,
  IcArticle,
  IcEdit,
  IcEye,
  IcWarning,
} from '@/assets/images';
import { EllipsisTooltip } from '@/components';
import { excludeValues } from '@/components/DataGrid/utils';
import { DATE_FORMAT } from '@/config';
import { GenderList } from '@/constants/form_inputs';
import { IInmate } from '@/pages/JMS/Managament/types/inmate';
import { JmsManagementRoutes } from '@/pages/JMS/Managament/utils/utils';
import { maskSensitiveString } from '@/pages/MasterIndex/Persons/data';
import { formatPersonName } from '@/utils';

const fullName = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const firstName = params.row.masterName?.firstName || '';
    const lastName = params.row.masterName?.lastName || '';
    const middleName = params.row.masterName?.middleName || '';
    return formatPersonName({ firstName, middleName, lastName });
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
  valueGetter: (_, row) => {
    const firstName = row.masterName?.firstName || '';
    const lastName = row.masterName?.lastName || '';
    const middleName = row.masterName?.middleName || '';
    return formatPersonName({ firstName, middleName, lastName });
  },
  sortComparator: (a, b) =>
    (a?.toLowerCase() || '').localeCompare(b?.toLowerCase() || ''),
});

const inmateId = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return params.row.iid;
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues([
      'isNotEmpty',
      'isEmpty',
      'isAnyOf',
      'contains',
      'doesNotContain',
      'startsWith',
      'endsWith',
    ]),
  ),
});

const gender = (): Partial<GridColDef> => ({
  filterable: false,
  sortable: true,
  renderCell: (params) =>
    GenderList[params.row.masterName?.sex as keyof typeof GenderList],
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
  valueGetter: (_, row) => row.masterName?.sex,
});

const dateOfBirth = (): Partial<GridColDef> => ({
  sortable: true,
  filterOperators: getGridDateOperators(false).filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
  renderCell: (params) => {
    return params.row.masterName?.birthday
      ? moment(params.row.masterName?.birthday).format(DATE_FORMAT)
      : '';
  },
  valueGetter: (_, row) => row.masterName?.birthday,
});

const housing = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return params.row.housing?.name ? (
      <Box
        sx={{
          width: params.colDef.width,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <EllipsisTooltip style={{ fontSize: '0.875rem' }}>
          {params.row.housing.parent?.parent?.parent?.parent?.name} -{' '}
          {params.row.housing.parent?.parent?.parent?.name} -{' '}
          {params.row.housing.parent?.parent?.name} -{' '}
          {params.row.housing.parent?.name} - {params.row.housing.name}
        </EllipsisTooltip>
      </Box>
    ) : (
      '/'
    );
  },
});

const location = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return params.row.location?.name ? (
      <Box
        sx={{
          width: params.colDef.width,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <EllipsisTooltip style={{ fontSize: '0.875rem' }}>
          {params.row.location.parent?.parent?.parent?.parent?.name} -{' '}
          {params.row.location.parent?.parent?.parent?.name} -{' '}
          {params.row.location.parent?.parent?.name} -{' '}
          {params.row.location.parent?.name} - {params.row.location.name}
        </EllipsisTooltip>
      </Box>
    ) : (
      '/'
    );
  },
});

const ssn = (): Partial<GridColDef> => ({
  renderCell: (params) => maskSensitiveString(params.row.masterName?.ssn),
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const status = (): Partial<GridColDef> => ({
  filterable: false,
  renderCell: (params) => params.row.masterName?.inmateStatus,
});

const actions = (
  onView?: (inmate?: IInmate) => void,
  onEdit?: (inmate?: IInmate) => void,
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    param: IInmate,
  ) => void,
  navigate?: (path: string) => void,
): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  renderCell: (params: { row: IInmate }) => (
    <div className="flex items-center">
      <IconButton onClick={() => onView?.(params.row)}>
        <IcEye className="text-gray-400" />
      </IconButton>
      <IconButton onClick={() => onEdit?.(params.row)}>
        <IcEdit className="text-gray-400" />
      </IconButton>
      <IconButton onClick={(e) => openMenu?.(e, params?.row)}>
        <IcAddNote className="text-gray-400" />
      </IconButton>
      <IconButton
        onClick={() => {
          navigate?.(
            `${JmsManagementRoutes.EditInmateContextNotes(
              params.row._id,
            )}?context=inmate`,
          );
        }}
      >
        <IcArticle className="text-gray-400" />
      </IconButton>
    </div>
  ),
});

const flags = (): Partial<GridColDef> => ({
  filterable: false,
  renderCell: (params) => {
    const warnings = params.row.flags || [];

    return (
      <Box display="flex" gap={1}>
        {warnings.includes('warning') && (
          <Tooltip title="Warning">
            <IcWarning color="orange" />
          </Tooltip>
        )}
        {warnings.includes('medical') && (
          <Tooltip title="Medical Alert">
            <IcWarning color="red" />
          </Tooltip>
        )}
        {warnings.includes('suicideWatch') && (
          <Tooltip title="Suicide Watch">
            <Chip
              label="Suicide Watch"
              sx={{
                backgroundColor: '#33363F',
                color: '#fff',
                fontWeight: 500,
                fontSize: '12px',
                height: '25px',
                borderRadius: '6px',
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  },
});

export const inmateCols = (
  onView?: (inmate?: IInmate) => void,
  onEdit?: (inmate?: IInmate) => void,
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    param: IInmate,
  ) => void,
  navigate?: (path: string) => void,
) => {
  return {
    fullName,
    inmateId,
    gender,
    dateOfBirth,
    housing,
    location,
    ssn,
    status,
    flags,
    actions: () => actions(onView, onEdit, openMenu, navigate),
  };
};
